import { Injectable } from "@angular/core";
import { HttpService } from "../../../infrastructure/http/http";
import { HttpParams } from "@angular/common/http";
import { UserService } from "../../../domain/user-service";
import { ChannelRepository } from "../../../domain/channel-repository";
import { Subject, BehaviorSubject } from "rxjs";
import { RouteSecEventType } from "../../../domain/route-sec-event-type";
import { LanguageToggleTarget } from "../../../domain/language-toggle-target";
import { SecEventCategory } from "../../../domain/sec-event-category";
import { TimeType } from "../../../domain/time-type";
import { ShowRepository } from "../../../domain/show-repository";
import { SecEventType } from "../../../domain/sec-events";

@Injectable({
  providedIn: "root",
})
export class SecEventsService {
  constructor(
    private channelRepository: ChannelRepository,
    private httpService: HttpService,
    private userService: UserService,
    private showRepository: ShowRepository,
  ) {}

  onLanguageChange = new Subject<any>();
  onEventToggle = new Subject<any>();
  onTimeChange = new Subject<any>();
  onFilterSecEvents = new BehaviorSubject<any>(
    new Set(Object.values(SecEventType)),
  );
  onAddNewSecEvent = new Subject<any>();
  onDeleteSecEvent = new Subject<any>();
  onAddAudioTrack = new Subject<any>();
  onCreateNewMarker = new Subject<any>();

  updateTime(
    eventType: SecEventCategory,
    showId: string,
    idMethod: string | number,
    originalOffset: string,
    timeType: TimeType | string,
    newTime: string,
  ) {
    const changedProperty = `new${timeType}`;
    let idMethodProperty;
    switch (eventType) {
      case SecEventCategory.Graphics:
        idMethodProperty = "graphicName";
        break;
      case SecEventCategory.FloatingMedia:
        idMethodProperty = "floatingMediaId";
        break;
      case SecEventCategory.Signal:
        idMethodProperty = "signalEventId";
        break;
    }
    return this.httpService
      .post("Scheduler/EditEvent", {
        eventType,
        showId,
        [idMethodProperty]: idMethod,
        originalOffset,
        [changedProperty]: newTime,
      })
      .then((updatedTimes) => {
        this.onTimeChange.next({
          eventType,
          identifier: idMethod,
          showId,
          originalOffset,
          ...updatedTimes,
        });
      });
  }

  deleteEvent(
    eventType: SecEventCategory,
    showId: string,
    idMethod: string | number,
    originalOffset: string,
  ) {
    let idMethodProperty;
    switch (eventType) {
      case SecEventCategory.Graphics:
        idMethodProperty = "graphicName";
        break;
      case SecEventCategory.FloatingMedia:
        idMethodProperty = "floatingMediaId";
        break;
      case SecEventCategory.Signal:
        idMethodProperty = "signalEventId";
        break;
    }
    return this.httpService
      .post("Scheduler/DeleteEvent", {
        eventType,
        showId,
        [idMethodProperty]: idMethod,
        originalOffset,
      })
      .then(() => {
        this.onDeleteSecEvent.next({
          showId,
          eventType,
          identifier: idMethod,
          originalOffset,
        });
      });
  }

  createEvent(showId: string, eventType: SecEventCategory, extraInfo) {
    const EXTRA_INFO_PROPERTIES = {
      [SecEventCategory.Signal]: "signalType",
      [SecEventCategory.FloatingMedia]: "floatingMediaId",
      [SecEventCategory.Graphics]: "graphicName",
    };

    return this.httpService
      .post("Scheduler/CreateEvent", {
        showId,
        eventType,
        [EXTRA_INFO_PROPERTIES[eventType]]: extraInfo,
      })
      .then((newEvent) => {
        const newSecEvent: any = { properties: newEvent };

        switch (eventType) {
          case SecEventCategory.Graphics:
            newSecEvent.type = SecEventType.Graphics;
            break;
          case SecEventCategory.FloatingMedia:
            newSecEvent.type = SecEventType.FloatingMedia;
            break;
          case SecEventCategory.Signal:
            // @TODO
            break;
        }
        this.onAddNewSecEvent.next({ showId, newSecEvent });
      });
  }

  addAudioTrackFromPopup(showId, track) {
    this.onAddAudioTrack.next({ showId, track });
  }

  createNewMarker(markerData) {
    this.onCreateNewMarker.next(markerData);
  }
}
